/* eslint-disable @typescript-eslint/no-empty-function */
import sync from 'framesync';
import { throttle } from 'lodash-es';
import { animate } from 'animejs';
import { v4 } from 'uuid';
import type {
  BoundingClientRect,
  CachedPositionData,
  ChildBoundingClientRect,
  ItemTransform,
  ItemPosition,
  WrapGridArguments,
} from './useAnimatedGrid.types';

const DATASET_KEY = 'animateGridId';

// return a function that take a reference to a grid dom node and optional config
export const wrapGrid = (
  container: HTMLElement,
  {
    duration = 250,
    stagger = 0,
    easing = 'outCubic',
    onStart = () => { },
    onEnd = () => { },
  }: WrapGridArguments = {}
) => {
  let mutationsDisabled = false;

  // all cached position data, and in-progress tween data, is stored here
  const cachedPositionData: CachedPositionData = {};

  // initially and after every transition, record element positions
  const recordPositions = (
    elements: HTMLCollectionOf<HTMLElement> | HTMLElement[]
  ) => {
    const gridBoundingClientRect = container.getBoundingClientRect();
    Array.from(elements).forEach(el => {
      if (typeof el.getBoundingClientRect !== 'function') {
        return;
      }
      if (!el.dataset[DATASET_KEY]) {
        const newId = `${Math.random()}`;
        el.dataset[DATASET_KEY] = newId;
      }
      const animateGridId = el.dataset[DATASET_KEY] as string;

      if (!cachedPositionData[animateGridId]) {
        cachedPositionData[animateGridId] = {} as ItemPosition;
      }

      const rect = getGridAwareBoundingClientRect(gridBoundingClientRect, el);
      const itemPosition = cachedPositionData[animateGridId];
      itemPosition.rect = rect;
      itemPosition.gridBoundingClientRect = gridBoundingClientRect;

      if (!itemPosition.transform) itemPosition.transform = {} as ItemTransform;
      const currentTransform = extractTransformProperties(el);
      itemPosition.transform.translateX = currentTransform.translateX;
      itemPosition.transform.translateY = currentTransform.translateY;
      itemPosition.transform.scaleX = currentTransform.scaleX;
      itemPosition.transform.scaleY = currentTransform.scaleY;
    });
  };
  recordPositions(container.children as HTMLCollectionOf<HTMLElement>);

  const onMutation = (
    mutationsList: MutationRecord[] | 'forceGridAnimation'
  ) => {
    // check if we care about the mutation
    if (mutationsList !== 'forceGridAnimation') {
      const relevantMutationHappened = mutationsList.filter(
        (m: MutationRecord) =>
          m.attributeName === 'class' ||
          m.addedNodes.length ||
          m.removedNodes.length
      ).length;
      if (!relevantMutationHappened) {
        return;
      }
      if (mutationsDisabled) return;
    }

    const gridBoundingClientRect = container.getBoundingClientRect();
    const childrenElements = Array.from(container.children) as HTMLElement[];

    // stop current transitions and remove transforms on transitioning elements
    childrenElements
      .filter(el => {
        const itemPosition =
          cachedPositionData[el.dataset[DATASET_KEY] as string];
        if (itemPosition && itemPosition.stopTween) {
          itemPosition.stopTween();
          delete itemPosition.stopTween;
          return true;
        }
      })
      .forEach(el => {
        el.style.transform = '';
        const firstChild = el.children[0] as HTMLElement;
        if (firstChild) {
          firstChild.style.transform = '';
        }
      });
    
    // update all item positions
    const animatedGridChildren = childrenElements
      .map(el => ({
        childCoords: {} as ChildBoundingClientRect,
        el,
        boundingClientRect: getGridAwareBoundingClientRect(
          gridBoundingClientRect,
          el
        ),
      }))
      .filter(({ el, boundingClientRect }) => {
        const itemPosition =
          cachedPositionData[el.dataset[DATASET_KEY] as string];
        // don't animate the initial appearance of elements,
        // just cache their position so they can be animated later
        if (!itemPosition) {
          recordPositions([el]);
          return false;
        } else if (
          boundingClientRect.top === itemPosition.rect.top &&
          boundingClientRect.left === itemPosition.rect.left &&
          boundingClientRect.width === itemPosition.rect.width &&
          boundingClientRect.height === itemPosition.rect.height
        ) {
          // if it hasn't moved, dont animate it
          return false;
        }
        return true;
      });

    // having more than one child in the animated item is not supported
    animatedGridChildren.forEach(({ el }) => {
      if (Array.from(el.children).length > 1) {
        throw new Error(
          'Make sure every grid item has a single container element surrounding its children'
        );
      }
    });

    if (!animatedGridChildren.length) {
      return;
    }

    const animatedElements = animatedGridChildren.map(({ el }) => el);
    disableMutationsWhileFunctionRuns(() => onStart(animatedElements));

    const completionPromises: Array<Promise<unknown>> = [];

    animatedGridChildren
      // do this measurement first so as not to cause layout thrashing
      .map(data => {
        const firstChild = data.el.children[0] as HTMLElement;
        // different transform origins give different effects. "50% 50%" is default
        if (firstChild) {
          data.childCoords = getGridAwareBoundingClientRect(
            gridBoundingClientRect,
            firstChild
          );
        }
        return data;
      })
      .forEach(
        (
          {
            el,
            boundingClientRect: { top, left, width, height },
            childCoords: { top: childTop, left: childLeft },
          },
          i
        ) => {
          const firstChild = el.children[0] as HTMLElement;
          const itemPosition =
            cachedPositionData[el.dataset[DATASET_KEY] as string];

          const oldRect = itemPosition.rect;
          
          // const oldTransform = itemPosition.transform;
          // const itemTransform: ItemTransform = {
          //   scaleX: (oldRect.width * oldTransform.scaleX) / width,
          //   scaleY: (oldRect.height * oldTransform.scaleY) / height,
          //   translateX: (oldRect.left + oldTransform.translateX) - left,
          //   translateY: (oldRect.top + oldTransform.translateY) - top,
          // };
          const itemTransform: ItemTransform = {
            scaleX: oldRect.width / width,
            scaleY: oldRect.height / height,
            translateX: oldRect.left - left,
            translateY: oldRect.top - top,
          };
          itemPosition.transform.scaleX = itemTransform.scaleX;
          itemPosition.transform.scaleY = itemTransform.scaleY;
          itemPosition.transform.translateX = itemTransform.translateX;
          itemPosition.transform.translateY = itemTransform.translateY;

          el.style.transformOrigin = '0 0';
          if (firstChild && childLeft === left && childTop === top) {
            firstChild.style.transformOrigin = '0 0';
          }

          let cachedResolve: () => void;

          const completionPromise = new Promise(resolve => {
            cachedResolve = resolve as () => void;
          });

          completionPromises.push(completionPromise);

          applyCoordTransform(el, itemTransform, { immediate: true });
          
          // Create a uid for the animation
          const uid = v4();
          itemPosition.tweenUid = uid;
          // now start the animation
          const startAnimation = () => {
            const animation = animate(itemTransform, {

              // target values
              translateX: 0,
              translateY: 0,
              scaleX: 1,
              scaleY: 1,

              duration,
              ease: easing,
              onUpdate: () => {
                // if the tweenUid has changed, another animation has started.
                if (uid !== itemPosition.tweenUid) return;

                applyCoordTransform(el, itemTransform, { uid }, itemPosition);
                // this helps prevent layout thrashing
                sync.postRender(() => recordPositions([el]));
              },
              onComplete: cachedResolve,
            });
            itemPosition.stopTween = animation.pause.bind(animation);
          };

          if (typeof stagger !== 'number' || stagger === 0) {
            startAnimation();
          } else {
            const timeoutId = setTimeout(() => {
              sync.update(startAnimation);
            }, stagger * i);
            itemPosition.stopTween = () => clearTimeout(timeoutId);
          }
        }
      );

    Promise.all(completionPromises).then(() => {
      onEnd(animatedElements);
    });
  };

  const disableMutationsWhileFunctionRuns = (func: () => void) => {
    mutationsDisabled = true;
    func();
    setTimeout(() => {
      mutationsDisabled = false;
    }, 0);
  };

  // Add listeners to keep track of changes in the grid

  const throttledResizeListener = throttle(() => {
    const bodyElement = document.querySelector('body');
    const containerIsNoLongerInPage =
      bodyElement && !bodyElement.contains(container);
    if (!container || containerIsNoLongerInPage) {
      window.removeEventListener('resize', throttledResizeListener);
    }
    recordPositions(container.children as HTMLCollectionOf<HTMLElement>);
  }, 250);
  window.addEventListener('resize', throttledResizeListener);

  const throttledScrollListener = throttle(() => {
    recordPositions(container.children as HTMLCollectionOf<HTMLElement>);
  }, 20);
  container.addEventListener('scroll', throttledScrollListener);

  const observer = new MutationObserver(onMutation);
  observer.observe(container, {
    childList: true,
    attributes: true,
    subtree: true,
    attributeFilter: ['class'],
  });
  const unwrapGrid = () => {
    window.removeEventListener('resize', throttledResizeListener);
    container.removeEventListener('scroll', throttledScrollListener);
    observer.disconnect();
  };
  const forceGridAnimation = () => onMutation('forceGridAnimation');
  return { unwrapGrid, forceGridAnimation };
};

// the function used during the tweening
const applyCoordTransform = (
  el: HTMLElement,
  { translateX, translateY, scaleX, scaleY }: ItemTransform,
  { immediate, uid }: { immediate?: boolean, uid?: string } = {},
  itemPosition: { tweenUid?: string } = {}
): void => {
  const isFinished =
    translateX === 0 && translateY === 0 && scaleX === 1 && scaleY === 1;
  
  const styleEl = () => {
    if (uid && uid !== itemPosition.tweenUid) {
      return;
    }
    el.style.transform = isFinished
      ? ''
      : `translateX(${translateX}px) translateY(${translateY}px) scaleX(${scaleX}) scaleY(${scaleY})`;
  };

  if (immediate) {
    styleEl();
  } else {
    sync.render(styleEl);
  }
  const firstChild = el.children[0] as HTMLElement;
  if (firstChild) {
    const styleChild = () => {
      firstChild.style.transform = isFinished
        ? ''
        : `scaleX(${1 / scaleX}) scaleY(${1 / scaleY})`;
    };
    if (immediate) {
      styleChild();
    } else {
      sync.render(styleChild);
    }
  }
};

// in order to account for scroll, (which we're not listening for)
// always cache the item's position relative
// to the top and left of the grid container
const getGridAwareBoundingClientRect = (
  gridBoundingClientRect: BoundingClientRect,
  el: HTMLElement
): BoundingClientRect => {
  const { top, left, width, height } = el.getBoundingClientRect();
  const rect = { top, left, width, height };
  rect.top -= gridBoundingClientRect.top;
  rect.left -= gridBoundingClientRect.left;
  // if an element is display:none it will return top: 0 and left:0
  // rather than saying it's still in the containing element
  // so we need to use Math.max to make sure the coordinates stay
  // within the container
  rect.top = Math.max(rect.top, 0);
  rect.left = Math.max(rect.left, 0);
  return rect;
};

/**
 * returns the transform properties of an element
 * Works in Chrome, Firefox, Safari, Edge
 */
function extractTransformProperties(element: HTMLElement): ItemTransform {
  const style = window.getComputedStyle(element);
  const matrix = new DOMMatrixReadOnly(style.transform);
  return {
    translateX: matrix.m41,
    translateY: matrix.m42,
    scaleX: matrix.a,
    scaleY: matrix.d,
  };
}